import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { BarChart3, CalendarDays, FlaskConical, CircleHelp, ChevronLeft, ChevronRight, LayoutGrid, LifeBuoy, LocateFixed, LogOut, Menu, Settings, Moon, Pause, Play, Radio, Smartphone, Sun, Luggage, TrendingUp } from "lucide-react";
import type { Board, User } from "../lib/api";
import { addDays, dayLabel, hhmm, longDate } from "../lib/format";
import { FROM_COPOPT } from "../lib/days-ahead";

type View = "board" | "ramp" | "settings" | "history" | "schedule" | "bags" | "forecast" | "rules" | "support" | "sandbox";
type Look = "airline" | "status" | "classic";

interface Props {
  board: Board | undefined;
  user: User;
  now: number;
  today: string;
  date: string;
  onDate: (d: string) => void;
  /** Jump to today and scroll the timeline to the current time. */
  onNow: () => void;
  live: boolean;
  dark: boolean;
  onDark: (v: boolean) => void;
  onClock: (action: "play" | "pause" | "speed", speed?: number) => void;
  onLogout: () => void;
  onHelp: () => void;
  view: View;
  onView: (v: View) => void;
  /** How bars are painted, and the reader's way of changing it. */
  look: Look;
  onLook: (v: Look) => void;
  canPlan: boolean;
  /** For administrators: Live, or the sandbox they are working in. Null for everyone else. */
  sandbox?: "live" | { id: number; name: string } | null;
}

const SPEEDS = [1, 10, 60, 300];

/** The pages behind the icons, in the order they are shown. Settings is the planners'. */
const PAGES: Array<{ view: View; label: string; title: string; icon: React.ReactNode; planners?: boolean }> = [
  { view: "schedule", label: "Schedule", title: "Schedule: every flight for the days ahead; build flights and park aircraft in storage", icon: <CalendarDays className="size-4" /> },
  { view: "forecast", label: "Forecast", title: "Forecast: passengers at security, and lanes needed", icon: <TrendingUp className="size-4" /> },
  { view: "bags", label: "Bags", title: "Bags: counts per flight from the bag system", icon: <Luggage className="size-4" /> },
  { view: "history", label: "History and figures", title: "History: past days, punctuality, who changed what", icon: <BarChart3 className="size-4" /> },
  { view: "support", label: "Support", title: "Support: report a problem or ask for a feature", icon: <LifeBuoy className="size-4" /> },
  { view: "settings", label: "Settings and rules", title: "Settings and rules", icon: <Settings className="size-4" />, planners: true },
];

/**
 * The header is one row that never widens the page: on a wide screen every page has
 * its own button; below that they fold into one menu, and whatever is left is a strip
 * that scrolls on its own rather than pushing the board sideways.
 */
export function TopBar(p: Props) {
  const tz = p.board?.airport.timezone ?? "UTC";
  const clock = p.board?.clock;
  const secs = new Date(p.now).getUTCSeconds();
  const pages = PAGES.filter((pg) => !pg.planners || p.canPlan);
  const toggle = (v: View) => p.onView(p.view === v ? "board" : v);
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-panel px-3 sm:px-4 print:hidden">
      <div className="flex min-w-0 shrink-0 items-center gap-2">
        <div className="hidden size-8 place-items-center rounded-lg bg-brand text-brand-ink sm:grid">
          <svg viewBox="0 0 32 32" className="size-5">
            <path d="M8 20h16M11 14h10M14 8h4" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="hidden leading-tight md:block">
          <div className="text-[14px] font-bold tracking-tight">Vectro Allocate</div>
          <div className="text-[11px] text-ink-3">{p.board ? `${p.board.airport.iata} · ${p.board.airport.name}` : "Loading…"}</div>
        </div>
      </div>

      <div className="flex shrink-0 items-center rounded-lg border border-line md:ml-2">
        <button className="p-1 text-ink-2 hover:text-ink sm:p-1.5" onClick={() => p.onDate(addDays(p.date, -1))} aria-label="Previous day">
          <ChevronLeft className="size-4" />
        </button>
        <div className="min-w-[4.5rem] px-1 text-center leading-tight sm:min-w-24">
          <div className="text-[12.5px] font-semibold">{dayLabel(p.date, tz, p.today)}</div>
          <div className="hidden text-[10px] text-ink-3 md:block">{longDate(p.date, tz)}</div>
        </div>
        <button className="p-1 text-ink-2 hover:text-ink sm:p-1.5" onClick={() => p.onDate(addDays(p.date, 1))} aria-label="Next day">
          <ChevronRight className="size-4" />
        </button>
      </div>

      {p.sandbox && (
        <button
          className={clsx(
            "flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-[12px] font-semibold",
            p.sandbox === "live" ? "border-line text-ink-2 hover:text-ink" : "border-warn bg-warn/15 text-warn",
          )}
          onClick={() => p.onView("sandbox")}
          title="Live, or a sandbox to try rules in. Open Sandboxes to switch."
        >
          <FlaskConical className="size-3.5" />
          <span className="max-w-[9rem] truncate">{p.sandbox === "live" ? "Live" : `Sandbox: ${p.sandbox.name}`}</span>
        </button>
      )}
      {p.board?.projectedFrom && p.board.projectedFrom === FROM_COPOPT && (
        <span
          className="hidden shrink-0 rounded-lg border border-dashed border-line px-2 py-1 text-[11.5px] font-semibold text-ink-2 lg:inline"
          title="Beyond the airport feed. These flights are CopOpt's schedule for the day; the feed's own replace them as it publishes them."
        >
          CopOpt schedule
        </span>
      )}
      {p.board?.projectedFrom && p.board.projectedFrom !== FROM_COPOPT && (
        <span
          className="hidden shrink-0 rounded-lg border border-dashed border-line px-2 py-1 text-[11.5px] font-semibold text-ink-2 lg:inline"
          title={`Beyond the airport feed. These flights are last ${new Date(p.board.projectedFrom + "T12:00:00").toLocaleDateString(undefined, { weekday: "long" })}'s, for planning only.`}
        >
          Projected from {new Date(p.board.projectedFrom + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
        </span>
      )}
      <button
        className="flex shrink-0 items-center gap-1 rounded-lg border border-line px-1.5 py-1.5 text-[12px] font-semibold text-bad hover:border-bad sm:px-2"
        onClick={p.onNow}
        title="Go to today and scroll to the current time"
      >
        <LocateFixed className="size-3.5" /> <span className="hidden sm:inline">Now</span>
      </button>

      <div className="flex-1" />

      <div className="scroll-none flex min-w-0 items-center gap-2 overflow-x-auto">
        {clock && (
          <div className="flex shrink-0 items-center gap-2">
            <div className="text-right leading-tight">
              <div className="tabular font-mono text-[20px] font-semibold tracking-tight">
                {hhmm(p.now, tz)}
                <span className="hidden text-[12px] text-ink-3 sm:inline">:{String(secs).padStart(2, "0")}</span>
              </div>
              <div className="flex items-center justify-end gap-1 text-[10px] text-ink-3">
                <span className={clsx("inline-block size-1.5 rounded-full", p.live ? "bg-ok" : "bg-bad")} />
                {clock.mode === "replay" ? `Replay ${clock.speed}×` : "Live"}
              </div>
            </div>
            {clock.mode === "replay" && p.canPlan && (
              <div className="hidden items-center rounded-lg border border-line lg:flex">
                <button
                  className="p-1.5 text-ink-2 hover:text-ink"
                  onClick={() => p.onClock(clock.playing ? "pause" : "play")}
                  aria-label={clock.playing ? "Pause replay" : "Play replay"}
                >
                  {clock.playing ? <Pause className="size-4" /> : <Play className="size-4" />}
                </button>
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => p.onClock("speed", s)}
                    className={clsx("px-1.5 py-1 text-[11px] font-semibold", clock.speed === s ? "text-ink" : "text-ink-3 hover:text-ink-2")}
                  >
                    {s}×
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <FeedBadge board={p.board} />

        <div className="flex shrink-0 rounded-lg border border-line text-[12px] font-medium">
          <button
            className={clsx("flex items-center gap-1 rounded-l-lg px-2 py-1", p.view === "board" ? "bg-panel-2 text-ink" : "text-ink-3")}
            onClick={() => p.onView("board")}
            aria-label="Board view"
          >
            <LayoutGrid className="size-3.5" /> <span className="hidden sm:inline">Board</span>
          </button>
          <button
            className={clsx("flex items-center gap-1 rounded-r-lg px-2 py-1", p.view === "ramp" ? "bg-panel-2 text-ink" : "text-ink-3")}
            onClick={() => p.onView("ramp")}
            aria-label="Ramp view"
          >
            <Smartphone className="size-3.5" /> <span className="hidden sm:inline">Ramp</span>
          </button>
        </div>

        <div className="hidden shrink-0 items-center gap-2 2xl:flex" data-pages="wide">
          {pages.slice(0, 3).map((pg) => (
            <PageButton key={pg.view} page={pg} active={p.view === pg.view} onClick={() => toggle(pg.view)} />
          ))}
          <button className="rounded-lg p-2 text-ink-2 hover:bg-panel-2" onClick={p.onHelp} aria-label="Help" title="Help: ask a question or read the guide">
            <CircleHelp className="size-4" />
          </button>
          {pages.slice(3).map((pg) => (
            <PageButton key={pg.view} page={pg} active={p.view === pg.view} onClick={() => toggle(pg.view)} />
          ))}
          <select
            className="rounded-lg border border-line bg-panel px-1.5 py-1 text-[11.5px] text-ink-2"
            value={p.look}
            onChange={(e) => p.onLook(e.target.value as Look)}
            aria-label="How bars are coloured"
            title="How bars are coloured on the board"
          >
            <option value="airline">Airline</option>
            <option value="status">Status</option>
            <option value="classic">Classic</option>
          </select>
          <button className="rounded-lg p-2 text-ink-2 hover:bg-panel-2" onClick={() => p.onDark(!p.dark)} aria-label="Toggle dark mode">
            {p.dark ? <Sun className="size-4" /> : <Moon className="size-4" />}
          </button>
          <div className="text-right leading-tight">
            <div className="text-[12px] font-semibold">{p.user.name}</div>
            <div className="text-[10px] text-ink-3 capitalize">{p.user.role}</div>
          </div>
        </div>

        <MoreMenu p={p} pages={pages} toggle={toggle} />

        <button className="hidden shrink-0 rounded-lg p-2 text-ink-2 hover:bg-panel-2 sm:block" onClick={p.onLogout} aria-label="Sign out">
          <LogOut className="size-4" />
        </button>
      </div>
    </header>
  );
}

function PageButton({ page, active, onClick }: { page: (typeof PAGES)[number]; active: boolean; onClick: () => void }) {
  return (
    <button className={clsx("rounded-lg p-2 hover:bg-panel-2", active ? "bg-panel-2 text-ink" : "text-ink-2")} onClick={onClick} aria-label={page.label} title={page.title}>
      {page.icon}
    </button>
  );
}

/** Below the widest screens the pages, the look, the theme and signing out live here. */
function MoreMenu({ p, pages, toggle }: { p: Props; pages: typeof PAGES; toggle: (v: View) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => {
      window.removeEventListener("mousedown", close);
      window.removeEventListener("keydown", close);
    };
  }, [open]);
  const row = "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-[13px] hover:bg-panel-2";
  const pick = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };
  return (
    <div ref={ref} className="relative shrink-0 2xl:hidden" data-pages="menu">
      <button className={clsx("rounded-lg p-2 hover:bg-panel-2", open ? "bg-panel-2 text-ink" : "text-ink-2")} onClick={() => setOpen(!open)} aria-label="More" aria-expanded={open} title="Pages, look and sign out">
        <Menu className="size-4" />
      </button>
      {open && (
        <div className="absolute top-full right-0 z-50 mt-1 w-60 rounded-xl border border-line bg-panel p-1 shadow-2xl">
          <div className="px-2.5 py-1.5 leading-tight">
            <div className="text-[12.5px] font-semibold">{p.user.name}</div>
            <div className="text-[10.5px] text-ink-3 capitalize">{p.user.role}</div>
          </div>
          {pages.map((pg) => (
            <button key={pg.view} className={clsx(row, p.view === pg.view && "bg-panel-2")} onClick={pick(() => toggle(pg.view))} title={pg.title}>
              <span className="text-ink-3">{pg.icon}</span> {pg.label}
            </button>
          ))}
          <button className={row} onClick={pick(p.onHelp)}>
            <CircleHelp className="size-4 text-ink-3" /> Help
          </button>
          <div className="my-1 border-t border-line" />
          <label className="flex items-center gap-2 px-2.5 py-1.5 text-[13px]">
            <span className="flex-1">Bar colours</span>
            <select className="rounded-lg border border-line bg-panel px-1.5 py-1 text-[11.5px] text-ink-2" value={p.look} onChange={(e) => p.onLook(e.target.value as Look)} aria-label="How bars are coloured">
              <option value="airline">Airline</option>
              <option value="status">Status</option>
              <option value="classic">Classic</option>
            </select>
          </label>
          <button className={row} onClick={() => p.onDark(!p.dark)}>
            {p.dark ? <Sun className="size-4 text-ink-3" /> : <Moon className="size-4 text-ink-3" />} {p.dark ? "Light mode" : "Dark mode"}
          </button>
          <button className={clsx(row, "sm:hidden")} onClick={pick(p.onLogout)}>
            <LogOut className="size-4 text-ink-3" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function FeedBadge({ board }: { board: Board | undefined }) {
  if (!board) return null;
  const f = board.feed;
  const label = f.kind === "ylw-live" ? "YLW live feed" : f.kind === "cirium-replay" ? "Cirium (replay)" : "Cirium file";
  const age = f.lastUpdated ? Math.round((Date.now() - f.lastUpdated) / 60_000) : null;
  const stale = age !== null && age > 10;
  return (
    <div
      className="hidden shrink-0 items-center gap-1 rounded-lg border border-line px-2 py-1 text-[11px] text-ink-2 lg:flex"
      title={`${f.description}${f.lastUpdated ? ` · last update ${new Date(f.lastUpdated).toLocaleTimeString()}` : ""}`}
    >
      <Radio className={clsx("size-3.5", stale ? "text-bad" : "text-ok")} />
      {label}
      {age !== null && <span className="text-ink-3">· {age < 1 ? "just now" : `${age} min ago`}</span>}
    </div>
  );
}
